import React, { useState, useEffect } from "react";
import CSVReader from "react-csv-reader";
import { Row, Col } from "react-bootstrap";
import { months } from "../../../../util/month";
import { CSVLink } from "react-csv";
import { useSolarCalc } from "../../../../util/calculations";

const TargetFileSection = (props) => {
	const [showError, setShowError] = useState(false);
	const [showInfo, setShowInfo] = useState(false);
	const [data, setData] = useState([]);
	const [CSVData, setCSVData] = useState([]);
	const validHeader = ["month", "egrid", "irr", "temp", "unused"];
	const setValue = props.setValue;
	const SC = useSolarCalc();

	const parserOptions = {
		dynamicTyping: true,
		skipEmptyLines: true,
	};

	/**
	 * Handle on file change
	 * @param {array} data
	 * @param {obj} info
	 */
	const onChange = (data, info) => {
		setShowError(false);
		const headerRow = data[0];

		// Validate if data has correct header
		const hasCorrectHeader = arrayEquals(validHeader, headerRow);

		// Filter data rows
		const dataRows = data.filter((row, i) => i > 0);

		// Get months
		const dataMonths = dataRows.map((row) => row[0]);

		// Get values
		const dataValues = dataRows.map((row) => row.filter((vals, i) => i > 0));

		// Validate if rows has all 12 months
		const has12Months = arrayEquals(months, dataMonths);

		const filteredHeader = headerRow.filter((row, i) => i > 0);

		if (hasCorrectHeader && has12Months)
			handleCSVData(filteredHeader, dataMonths, dataValues);
		else setShowError(true);
	};

	/**
	 * Format CSV data
	 * @param {array} headers
	 * @param {array} months
	 * @param {array} values
	 */
	const handleCSVData = (headers, months, values) => {
		if (months.length === values.length) {
			const dataEntries = months.map((month, m) => {
				const entries = headers.map((header, h) => [
					`${month}_${header}`,
					header === "unused"
						? SC.parseFormatNumber(values[m][h], 2)
						: values[m][h],
				]);
				return entries;
			});

			const eGridValues = values.map((value) => value[0]);
			const totalEGrid = eGridValues.reduce(
				(a, b) => parseFloat(a) + parseFloat(b)
			);
			const unusedValues = values.map((value) => value[3]);
			const avgUnused = SC.parseFormatNumber(
				unusedValues.reduce((a, b) => parseFloat(a) + parseFloat(b)) / 12,
				3
			);

			props.setTotalEGrid(totalEGrid);
			props.setAvgUnused(avgUnused * 100);

			// Merge all entries into 1 entry
			const groupedEntries = dataEntries.reduce((a, b) => a.concat(b));

			groupedEntries.forEach((element) => {
				setValue(element[0], element[1]);
			});
		}
	};

	/**
	 * Handle file errors
	 * @param {string} error
	 */
	const handleInvalidFile = (error) => {
		console.log("error :", error);
		if (error != null) setShowError(true);
	};

	/**
	 * Check if both arrays are identical
	 * @param {array} a
	 * @param {array} b
	 * @returns boolean
	 */
	const arrayEquals = (a, b) => {
		return (
			Array.isArray(a) &&
			Array.isArray(b) &&
			a.length === b.length &&
			a.every((val, index) => val === b[index])
		);
	};

	useEffect(() => {
		if (props.data != null) setData(props.data);
	}, [props.data]);

	useEffect(() => {
		const entries = Object.entries(data);
		const rejectedKeys = [
			"id",
			"table_name",
			"TA_correction",
			"GA_correction",
			"other_corrections",
		];

		const filteredEntries = entries.filter(
			(entry) => !rejectedKeys.includes(entry[0])
		);

		// Format CSV data
		const csvData = months.map((month) => {
			let entries = filteredEntries.filter((entry) => entry[0].includes(month));
			// Remove month str from key
			entries = entries.map((entry) => {
				const keySplit = entry[0].split("_");
				return keySplit.length > 1 ? [keySplit[1], 0] : entry[0];
			});

			// Sort entries
			entries = entries.sort(function (a, b) {
				if (a[0] < b[0]) {
					return -1;
				}
				if (a[0] > b[0]) {
					return 1;
				}
				return 0;
			});

			// Format entries
			const formattedEntries = [["month", month], ...entries];

			// Convert from entries to object
			const csvDataObj = Object.fromEntries(formattedEntries);

			return csvDataObj;
		});

		setCSVData(csvData);
	}, [data]);

	return (
		<>
			<Row>
				<Col md="12">
					<h5>
						Upload CSV file{" "}
						<CSVLink
							data={CSVData}
							filename={"target.csv"}
							onMouseEnter={() => setShowInfo(true)}
							onMouseLeave={() => setShowInfo(false)}
						>
							<span className="glyphicon glyphicon-info-sign info-icon"></span>
						</CSVLink>
					</h5>
					{showInfo && (
						<div
							className="info-card"
							style={{ top: "0", left: "20%", width: "40%" }}
						>
							<h5>Target CSV file input</h5>

							<p>There are 2 ways to submit / edit data</p>
							<ul>
								<li>Form inputs</li>
								<li>CSV File (Only monthly data)</li>
								<span>** Click to download the CSV template</span>
							</ul>
						</div>
					)}
					<CSVReader
						strict={true}
						accept=".csv,text/csv,application/vnd.ms-excel"
						cssClass="csv-reader-input"
						onFileLoaded={onChange}
						onError={handleInvalidFile}
						parserOptions={parserOptions}
						inputId="targetInputs"
						inputName="targetInputs"
					/>
					{showError && (
						<span style={{ color: "red" }}>
							*Invalid file type or structure
						</span>
					)}
				</Col>
			</Row>
		</>
	);
};

export default TargetFileSection;
